import { Button, Dropdown, TextInput } from 'components/common'
import DateTime from 'components/common/input/dateTime/dateTime'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { updatePricingDetails } from 'utils/api/pricingApi'
import { PRICING_FORM_FIELDS } from 'utils/constants'
import { createFlatPricingData } from './utils'
import { updatePricingSchema } from 'utils/schema/updatePricingSchema'
import { yupResolver } from '@hookform/resolvers/yup'

const {
  BARCODE,
  PROMO,
  PROMO_END_DATE,
  PROMO_START_DATE,
  SPECIAL_PRICE,
  SPECIAL_PRICE_START_DATE,
  SPECIAL_PRICE_END_DATE,
  DEPARTMENT,
  ERP_BRAND,
  ERP_ITEM_DESC,
  ERP_PACK_INFO,
  ERP_PRICE,
  ERP_UOM,
  FAMILY,
  ITEM_NUMBER,
  ONLINE_DESC,
  ONLINE_PACK_INFO,
  AGGREGATOR_ID,
  QTY_PER_UOM,
  SECTION,
  STORE_ID,
  STORE_NAME,
  SUB_FAMILY,
  SLASH_PRICE,
  SLASH_PRICE_END_DATE,
  SLASH_PRICE_START_DATE,
  BLOCK_SALE_ON_POS,
} = PRICING_FORM_FIELDS

const UpdatePricingForm = ({
  pricingData,
  pricingId,
}: {
  pricingData: Record<string, any>
  pricingId: string
}) => {
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...pricingData,
      [BLOCK_SALE_ON_POS.key]: pricingData[BLOCK_SALE_ON_POS.key]
        ? 'Yes'
        : 'No',
    },
    resolver: yupResolver(updatePricingSchema),
  })

  /**
   * Will submit the form for updation
   */
  const handleUpdatePricingData = async (formData: any) => {
    const loading = toast.loading('Updating pricing data')
    try {
      const editableData = createFlatPricingData(formData)
      await updatePricingDetails(pricingId, editableData)
      cancelAndGoback()
      toast.success('Pricing Data updated successfully', { id: loading })
    } catch (error) {
      toast.error('Error updating pricing data!', { id: loading })
    }
  }
  /**
   * Will cancel and back to previous page
   */
  const cancelAndGoback = () => {
    reset()
    navigate(-1)
  }

  return (
    <form onSubmit={handleSubmit(handleUpdatePricingData)}>
      <div className="flex flex-wrap gap-x-14 gap-y-3">
        <div className="w-64">
          <TextInput
            register={register}
            label={STORE_NAME.label}
            name={STORE_NAME.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            label={ERP_ITEM_DESC.label}
            name={ERP_ITEM_DESC.key}
            register={register}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={ERP_PRICE.label}
            name={ERP_PRICE.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={STORE_ID.label}
            name={STORE_ID.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={ERP_UOM.label}
            name={ERP_UOM.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={ITEM_NUMBER.label}
            name={ITEM_NUMBER.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={QTY_PER_UOM.label}
            name={QTY_PER_UOM.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={BARCODE.label}
            name={BARCODE.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={ERP_PACK_INFO.label}
            name={ERP_PACK_INFO.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={SPECIAL_PRICE.label}
            name={SPECIAL_PRICE.key}
            error={errors[SPECIAL_PRICE.key]?.message as string}
          />
        </div>
        <div className="w-64">
          <DateTime
            label={SPECIAL_PRICE_START_DATE.label}
            name="specialPriceStartDate"
            register={register}
            error={errors[SPECIAL_PRICE_START_DATE.key]?.message as string}
          />
        </div>
        <div className="w-64">
          <DateTime
            label={SPECIAL_PRICE_END_DATE.label}
            name="specialPriceEndDate"
            register={register}
            error={errors[SPECIAL_PRICE_END_DATE.key]?.message as string}
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={DEPARTMENT.label}
            name={DEPARTMENT.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={ERP_BRAND.label}
            name={ERP_BRAND.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={SECTION.label}
            name={SECTION.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={ONLINE_DESC.label}
            name={ONLINE_DESC.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={FAMILY.label}
            name={FAMILY.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={SUB_FAMILY.label}
            name={SUB_FAMILY.key}
            disabled
          />
        </div>
        <div className="w-64">
          <Dropdown
            setValue={setValue}
            options={[
              { label: 'Inactive', value: '0' },
              { label: 'Active', value: '1' },
            ]}
            defaultValue={`${pricingData?.[PROMO.key]}`}
            label={PROMO.label}
            name={PROMO.key}
          />
        </div>
        <div className="w-64">
          <DateTime
            label={PROMO_START_DATE.label}
            name={PROMO_START_DATE.key}
            register={register}
            error={errors[PROMO_START_DATE.key]?.message as string}
          />
        </div>
        <div className="w-64">
          <DateTime
            label={PROMO_END_DATE.label}
            name={PROMO_END_DATE.key}
            register={register}
            error={errors[PROMO_END_DATE.key]?.message as string}
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={ONLINE_PACK_INFO.label}
            name={ONLINE_PACK_INFO.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={AGGREGATOR_ID.label}
            name={AGGREGATOR_ID.key}
            disabled
          />
        </div>
        <div className="w-64">
          <TextInput
            register={register}
            label={BLOCK_SALE_ON_POS.label}
            name={BLOCK_SALE_ON_POS.key}
            disabled
          />
        </div>

        <div className="w-64">
          <TextInput
            register={register}
            label={SLASH_PRICE.label}
            name={SLASH_PRICE.key}
            error={errors[SLASH_PRICE.key]?.message as string}
          />
        </div>
        <div className="w-64">
          <DateTime
            label={SLASH_PRICE_START_DATE.label}
            name={SLASH_PRICE_START_DATE.key}
            register={register}
            error={errors[SLASH_PRICE_START_DATE.key]?.message as string}
          />
        </div>
        <div className="w-64">
          <DateTime
            label={SLASH_PRICE_END_DATE.label}
            name={SLASH_PRICE_END_DATE.key}
            register={register}
            error={errors[SLASH_PRICE_END_DATE.key]?.message as string}
          />
        </div>
      </div>
      <div className="mt-10 flex gap-10">
        <Button buttonText="submit" className="w-40" />
        <Button
          buttonText="cancel"
          type="button"
          className="w-40"
          variant="outline"
          onClick={cancelAndGoback}
        />
      </div>
    </form>
  )
}

export default UpdatePricingForm
